<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div style="width: 100%;">
		<div class="d-flex align-center flex-fill pa-3" style="background-color: #fff;">
			<k-text-field
				v-model="filter.term"
				prepend-inner-icon="fa fa-filter"
				clear-icon="fa fa-times-circle"
				label="Search by title, ID, or author"
				background-color="#fff"
				class="pr-3"
				clearable
				single-line
				hide-details
				outlined
				dense
				@keydown.enter="load_assets"
			/>

			<k-select
				v-model="filter.asset_type"
				:items="asset_types"
				class="pr-3"
				solo outlined dense flat hide-details
			/>

			<k-select
				v-if="publish_status == 'unsubmitted'"
				v-model="filter.status"
				:items="statuses"
				class="pr-3"
				solo outlined dense flat hide-details
			/>

			<k-select
				v-model="filter.subject"
				:items="subjects"
				placeholder="All Subjects"
				class="pr-3"
				solo outlined dense flat hide-details
			/>

			<k-select
				v-model="filter.grade"
				:items="grades"
				placeholder="All Grades"
				class="pr-3"
				solo outlined dense flat hide-details
			/>

			<k-btn color="primary" @click="load_assets">
				Load Records
			</k-btn>
		</div>

		<div v-if="has_more" class="px-3 pb-2" style="background-color: #fff; font-size: 12px;">
			<i class="fas fa-circle-info mr-1"></i>Showing the {{ items.length }} most recently created matches. Narrow your filters to see others.
		</div>

		<k-data-table
			:items="items"
			:headers="headers"
			:options.sync="options"
			:no-data-text="loaded ? 'No matching records' : 'Use the filters above and click Load Records.'"
			class="k-asset-manager-table"
			width="100%"
			hide-default-footer
			fixed-header
			light
		>
			<template v-slot:item="{ item }">
				<tr class="k-table-row" @click="$emit('show_status', item)">
					<td
						v-for="(header, i) in headers"
						:key="'header-' + i"
						:class="[
							{'title-column': header.value == 'title'},
							{'center-text': header.align == 'center'},
							{'subject-column': header.value == 'subjects'},
						]"
					>
						<template v-if="header.value == 'title'"><i class="mr-1 fas" :class="item.asset.icon()"></i>{{ item.title }}</template>
						<span v-else-if="header.value == 'alignments'" :class="{'k-asset-manager-table-no-alignments': item.alignments == 0}">{{ item.alignments }}</span>
						<span v-else-if="header.value == 'status'" class="nobr-column" :class="`${item.status.color}--text text--darken-4`"><i :class="item.status.icon"></i> {{ item.status.text }}</span>
						<template v-else-if="header.value == 'created_at' || header.value == 'published_at'">{{ date_value(item[header.value]) }}</template>
						<k-tooltip v-else-if="header.value == 'approved_at'" :disabled="!item.approved_at" bottom>
							<template v-slot:activator="{ on }">
								<k-icon v-on="on" :color="item.approved_at ? 'green' : ''" small>{{ item.approved_at ? 'fas fa-file-check' : 'fas fa-minus' }}</k-icon>
							</template>
							Approved for publication {{ date_value(item.approved_at) }}
						</k-tooltip>
						<template v-else>{{ item[header.value] }}</template>
					</td>
				</tr>
			</template>
		</k-data-table>
	</div>
</template>

<script>
import { mapState } from 'vuex'

export default {
	name: 'AssetLookupTable',

	props: {
		// 'published' or 'unsubmitted'
		publish_status: {type: String, required: true},
		subjects: {type: Array, required: true},
		grades: {type: Array, required: true},
	},

	data() {
		return {
			loaded: false,
			has_more: false,
			items: [],
			filter: {
				term: undefined,
				asset_type: 'lesson',
				status: 'all',
				subject: undefined,
				grade: undefined,
			},
			options: {
				itemsPerPage: -1,
				sortBy: ['created_at'],
				sortDesc: [true],
			},
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		headers() {
			let is_published = this.publish_status == 'published'
			return [
				{ text: 'Title', align: 'start', sortable: true, width: '200px', value: 'title' },
				{ text: 'ID', align: 'start', sortable: true, value: 'id' },
				{ text: 'Author', align: 'center', sortable: true, value: 'creator_name' },
				{ text: 'Created', align: 'center', sortable: true, value: 'created_at' },
				!is_published && { text: 'Status', align: 'center', sortable: true, value: 'status', sort: (a, b) => a.text.localeCompare(b.text) },
				is_published && { text: 'Published', align: 'center', sortable: true, value: 'published_at' },
				is_published && { text: 'Reviewed', align: 'center', sortable: true, value: 'approved_at' },
				{ text: 'Subject', align: 'center', sortable: true, value: 'subjects' },
				{ text: 'Grade', align: 'center', sortable: false, value: 'grades' },
				{ text: 'Alignments', align: 'center', sortable: true, value: 'alignments' },
			].filter(Boolean)
		},

		asset_types() {
			return [
				{value: 'lesson', text: 'Lessons'},
				{value: 'activity', text: `${this.site_config.sparkl_app_name} Activities`},
				{value: 'resource', text: 'Resources'},
			]
		},

		// statuses that sit outside the publishing workflow
		statuses() {
			return [
				{value: 'all', text: '--- ALL ---'},
				{value: 'not_submitted', text: this.$get_publish_status_data(null).text},
				{value: 'unpublished', text: this.$get_publish_status_data('unpublished').text},
				{value: 'rejected', text: this.$get_publish_status_data('rejected').text},
			]
		},
	},

	methods: {
		async load_assets() {
			let payload = {
				user_id: this.user_info.user_id,
				publish_status: this.publish_status,
				asset_type: this.filter.asset_type,
			}
			if (this.filter.term) payload.search_term = this.filter.term
			if (this.publish_status == 'unsubmitted' && this.filter.status && this.filter.status != 'all') payload.status_filter = this.filter.status
			if (this.filter.subject && this.filter.subject != 'all') payload.subject = this.filter.subject
			if (this.filter.grade && this.filter.grade != 'all') payload.grade = this.filter.grade

			U.loading_start()
			let result = await new Promise(resolve => U.ajax('get_admin_assets', payload, resolve))
			U.loading_stop()

			if (result.status != 'ok') {
				console.error('load_assets error: ', result)
				return
			}

			this.loaded = true
			this.has_more = result.has_more
			this.items = (result.assets ?? []).map(x => {
				let is_lesson = payload.asset_type == 'lesson'
				let asset = is_lesson ? new Lesson(x) : new Resource(x)
				// flatten display values so k-data-table's built-in sorting works on them
				return {
					asset,
					asset_class_type: is_lesson ? 'lesson' : 'resource',
					title: asset.title(),
					id: is_lesson ? asset.lesson_id : asset.resource_id,
					creator_name: x.creator_name,
					created_at: x.created_at,
					status: this.$get_publish_status_data(x.publish_status),
					// dates from the asset log; approved_at is only set if the asset went through the review process
					published_at: x.review_history.published_at ?? x.published_date ?? null,
					approved_at: x.review_history.approved_at,
					subjects: x.subjects.map(s => s.text).join(', '),
					grades: x.grades.join(', '),
					alignments: asset.standards.length,
				}
			})
		},

		date_value(value) {
			if (empty(value)) return ''
			// these are mysql timestamps stored in GMT (same handling as AssetLog)
			let local_date = U.convert_to_local_date(value)
			return local_date != 'Invalid Date' ? local_date.toLocaleDateString() : value
		},
	},
}
</script>
